import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FolderPlus, Folder, ChevronLeft, FileText, Upload, X, Plus,
    MoreVertical, Trash2, Edit2, Check, Image, File, Shield,
    FolderOpen, Download, Eye
} from 'lucide-react';

const FolderManager = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // State for folders and files
    const [folders, setFolders] = useState([
        { id: 1, name: 'My Documents', files: [], color: 'blue', createdAt: new Date() },
        { id: 2, name: 'Certificates', files: [], color: 'green', createdAt: new Date() }
    ]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [selectedColor, setSelectedColor] = useState('blue');
    const [editingFolder, setEditingFolder] = useState(null);
    const [showOptionsMenu, setShowOptionsMenu] = useState(null);

    const folderColors = [
        { name: 'blue', bg: 'bg-blue-500', light: 'bg-blue-100' },
        { name: 'green', bg: 'bg-green-500', light: 'bg-green-100' },
        { name: 'purple', bg: 'bg-purple-500', light: 'bg-purple-100' },
        { name: 'orange', bg: 'bg-orange-500', light: 'bg-orange-100' },
        { name: 'pink', bg: 'bg-pink-500', light: 'bg-pink-100' },
        { name: 'cyan', bg: 'bg-cyan-500', light: 'bg-cyan-100' }
    ];

    const getColorClasses = (colorName) => {
        return folderColors.find(c => c.name === colorName) || folderColors[0];
    };

    // Create new folder
    const handleCreateFolder = () => {
        if (!newFolderName.trim()) return;

        const newFolder = {
            id: Date.now(),
            name: newFolderName.trim(),
            files: [],
            color: selectedColor,
            createdAt: new Date()
        };

        setFolders(prev => [...prev, newFolder]);
        setNewFolderName('');
        setSelectedColor('blue');
        setShowCreateModal(false);
    };

    // Delete folder
    const handleDeleteFolder = (folderId) => {
        setFolders(prev => prev.filter(f => f.id !== folderId));
        setShowOptionsMenu(null);
        if (currentFolder?.id === folderId) {
            setCurrentFolder(null);
        }
    };

    // Rename folder
    const handleRenameFolder = (folderId, newName) => {
        if (!newName.trim()) return;
        setFolders(prev => prev.map(f =>
            f.id === folderId ? { ...f, name: newName.trim() } : f
        ));
        setEditingFolder(null);
    };

    // Handle file upload to current folder
    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files);
        if (!currentFolder || files.length === 0) return;

        const newFiles = files.map(file => ({
            id: Date.now() + Math.random(),
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB',
            type: file.type,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
            uploadedAt: new Date()
        }));

        setFolders(prev => prev.map(f =>
            f.id === currentFolder.id
                ? { ...f, files: [...f.files, ...newFiles] }
                : f
        ));

        // Update current folder view
        setCurrentFolder(prev => ({
            ...prev,
            files: [...prev.files, ...newFiles]
        }));
    };

    // Delete file from folder
    const handleDeleteFile = (fileId) => {
        setFolders(prev => prev.map(f =>
            f.id === currentFolder.id
                ? { ...f, files: f.files.filter(file => file.id !== fileId) }
                : f
        ));
        setCurrentFolder(prev => ({
            ...prev,
            files: prev.files.filter(file => file.id !== fileId)
        }));
    };

    // Get file icon based on type
    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) return Image;
        if (fileType?.includes('pdf')) return FileText;
        return File;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
            <div className="max-w-5xl mx-auto">

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={handleFileUpload}
                />

                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => currentFolder ? setCurrentFolder(null) : navigate('/docs')}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                {currentFolder ? currentFolder.name : 'My Folders'}
                            </h1>
                            <p className="text-sm text-gray-500">
                                {currentFolder
                                    ? `${currentFolder.files.length} files`
                                    : `${folders.length} folders`
                                }
                            </p>
                        </div>
                    </div>

                    {!currentFolder ? (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                        >
                            <FolderPlus className="w-4 h-4" />
                            New Folder
                        </button>
                    ) : (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
                        >
                            <Upload className="w-4 h-4" />
                            Upload Files
                        </button>
                    )}
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-6 border border-white/60 min-h-[400px]">

                    {!currentFolder ? (
                        /* Folder Grid View */
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {folders.map((folder) => {
                                const colorClasses = getColorClasses(folder.color);

                                return (
                                    <div
                                        key={folder.id}
                                        className="relative group"
                                    >
                                        <button
                                            onClick={() => setCurrentFolder(folder)}
                                            className={`w-full p-6 rounded-2xl ${colorClasses.light} border-2 border-transparent 
                                                hover:border-${folder.color}-300 transition-all hover:shadow-lg active:scale-[0.98]`}
                                        >
                                            <div className="flex flex-col items-center gap-3">
                                                <div className={`w-16 h-16 ${colorClasses.bg} rounded-2xl flex items-center justify-center shadow-lg`}>
                                                    <Folder className="w-8 h-8 text-white" />
                                                </div>

                                                {editingFolder === folder.id ? (
                                                    <input
                                                        type="text"
                                                        defaultValue={folder.name}
                                                        className="text-center text-sm font-medium bg-white px-2 py-1 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        autoFocus
                                                        onBlur={(e) => handleRenameFolder(folder.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                handleRenameFolder(folder.id, e.target.value);
                                                            }
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                ) : (
                                                    <span className="text-sm font-medium text-gray-800 truncate max-w-full">
                                                        {folder.name}
                                                    </span>
                                                )}

                                                <span className="text-xs text-gray-500">
                                                    {folder.files.length} files
                                                </span>
                                            </div>
                                        </button>

                                        {/* Options Menu Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowOptionsMenu(showOptionsMenu === folder.id ? null : folder.id);
                                            }}
                                            className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow-sm"
                                        >
                                            <MoreVertical className="w-4 h-4 text-gray-500" />
                                        </button>

                                        {/* Options Dropdown */}
                                        {showOptionsMenu === folder.id && (
                                            <div className="absolute top-10 right-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-10 min-w-[140px]">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingFolder(folder.id);
                                                        setShowOptionsMenu(null);
                                                    }}
                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                    Rename
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteFolder(folder.id);
                                                    }}
                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Add Folder Card */}
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="p-6 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-400 
                                    hover:bg-blue-50/50 transition-all flex flex-col items-center justify-center gap-3 min-h-[160px]"
                            >
                                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
                                    <Plus className="w-8 h-8 text-gray-400" />
                                </div>
                                <span className="text-sm font-medium text-gray-500">Create Folder</span>
                            </button>
                        </div>
                    ) : (
                        /* Files View Inside Folder */
                        <div>
                            {currentFolder.files.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16">
                                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <FolderOpen className="w-12 h-12 text-gray-300" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-600 mb-2">This folder is empty</h3>
                                    <p className="text-sm text-gray-400 mb-6">Upload files to get started</p>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                                    >
                                        <Upload className="w-4 h-4" />
                                        Upload Files
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {currentFolder.files.map((file) => {
                                        const FileIcon = getFileIcon(file.type);

                                        return (
                                            <div
                                                key={file.id}
                                                className="relative group p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-all"
                                            >
                                                <div className="flex flex-col items-center gap-3">
                                                    {file.preview ? (
                                                        <img
                                                            src={file.preview}
                                                            alt={file.name}
                                                            className="w-16 h-16 object-cover rounded-xl"
                                                        />
                                                    ) : (
                                                        <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-sm">
                                                            <FileIcon className="w-8 h-8 text-gray-400" />
                                                        </div>
                                                    )}
                                                    <div className="text-center">
                                                        <p className="text-sm font-medium text-gray-800 truncate max-w-[120px]">
                                                            {file.name}
                                                        </p>
                                                        <p className="text-xs text-gray-400">{file.size}</p>
                                                    </div>
                                                </div>

                                                {/* Delete button */}
                                                <button
                                                    onClick={() => handleDeleteFile(file.id)}
                                                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        );
                                    })}

                                    {/* Add More Files */}
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="p-4 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-400 
                                            hover:bg-blue-50/50 transition-all flex flex-col items-center justify-center gap-2 min-h-[140px]"
                                    >
                                        <Upload className="w-8 h-8 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-500">Add Files</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Security Footer */}
                <div className="mt-6 flex justify-center">
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-50 border border-emerald-200 rounded-full">
                        <Shield className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">
                            Your data is secured, safe, and always in your control.
                        </span>
                    </div>
                </div>
            </div>

            {/* Create Folder Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Create New Folder</h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Folder Name
                                </label>
                                <input
                                    type="text"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder="Enter folder name"
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Folder Color
                                </label>
                                <div className="flex gap-3">
                                    {folderColors.map((color) => (
                                        <button
                                            key={color.name}
                                            onClick={() => setSelectedColor(color.name)}
                                            className={`w-10 h-10 rounded-xl ${color.bg} transition-all ${selectedColor === color.name
                                                    ? 'ring-4 ring-offset-2 ring-gray-300 scale-110'
                                                    : 'hover:scale-105'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim()}
                                className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-200 disabled:text-gray-400"
                            >
                                Create Folder
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FolderManager;
